/* eslint-disable jsx-a11y/label-has-associated-control */
"use client";

import clsx from "clsx";
import { Control, Controller, FieldValues, Path } from "react-hook-form";

interface RadioOption {
  value: string;
  label: string;
  isNegative?: boolean;
}

interface FormRadioProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  label?: string;
  options: RadioOption[];
  containerClassName?: string;
}

export default function FormRadio<T extends FieldValues>({
  name,
  control,
  label,
  options,
  containerClassName = "",
}: FormRadioProps<T>) {
  const generateRadioOptions = (selectedValue: string, onChange: (value: string) => void) => {
    return options.map((option) => (
      <label
        key={option.value}
        className={clsx(
          "relative flex h-[60px] cursor-pointer items-center rounded-2xl border",
          "transition-all duration-300",
          "min-w-0 md:flex-1 md:basis-0",
          "mb-2 last:mb-0 md:mb-0 md:mr-2 md:last:mr-0",
          selectedValue === option.value
            ? option.isNegative
              ? "border-[#ff2a2a] bg-[#ff2a2a]/5"
              : "border-[#22C55E] bg-[#22C55E]/5"
            : "border-secondary-light bg-secondary-light",
          option?.isNegative ? "hover:border-[#ff2a2a]" : "hover:border-[#22C55E]",
        )}
      >
        <input
          type="radio"
          value={option.value}
          checked={selectedValue === option.value}
          onChange={() => onChange(option.value)}
          className="peer sr-only"
        />
        <div className="flex w-full items-center gap-4 px-4">
          {" "}
          <span
            className={clsx(
              "h-4 w-4 shrink-0 rounded-full border",
              "relative transition-all duration-300",
              "after:absolute after:left-1/2 after:top-1/2",
              "after:h-2 after:w-2 after:-translate-x-1/2 after:-translate-y-1/2",
              "after:rounded-full after:transition-all after:duration-300",
              selectedValue === option.value
                ? option.isNegative
                  ? "border-[#ff2a2a] after:bg-[#ff2a2a]"
                  : "border-[#22C55E] after:bg-[#22C55E]"
                : "border-gray-400 after:bg-transparent",
            )}
          />
          <span className="inter truncate text-sm font-medium text-gray-800"> {option.label}</span>
        </div>
      </label>
    ));
  };

  return (
    <Controller<T>
      name={name}
      control={control}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <div className="w-full space-y-2 pt-2">
          {label && <label className="inter mb-2 block text-sm font-semibold text-[#000000]">{label}</label>}
          <div className="flex w-full flex-col gap-3 md:flex-row">{generateRadioOptions(value, onChange)}</div>
          {error && <div className="mt-1 text-sm font-semibold text-red-500 rtl:text-right">{error.message}</div>}
        </div>
      )}
    />
  );
}
